import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";
import AuthPageLayout from "./AuthPageLayout";
import {
  ChannelToggle,
  DevOtpHint,
  EMAIL_RE,
  ErrorText,
  MOBILE_RE,
  OtpInput,
  ctaClass,
  fieldClass,
  labelClass,
  useCountdown,
} from "./OtpAuthParts";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { setUserTokens } from "@/lib/userSession";

function cleanTarget(channel, value) {
  return channel === "email" ? value.trim().toLowerCase() : value.replace(/\D/g, "");
}

export default function Login() {
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const returnTo = typeof from === "string" && from.startsWith("/") && !from.startsWith("//") && !from.startsWith("/login") ? from : "/profile";
  const { setUser } = useAuth();
  const [channel, setChannel] = useState("mobile");
  const [value, setValue] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [otpLength, setOtpLength] = useState(6);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [totp, setTotp] = useState("");
  const [error, setError] = useState("");
  const [otpError, setOtpError] = useState(0);
  const [deliveredVia, setDeliveredVia] = useState(""); // "email" when mobile gate is off
  const [deliveryTarget, setDeliveryTarget] = useState("");
  const [left, restartCountdown] = useCountdown(sent);

  const target = cleanTarget(channel, value);
  const valid = channel === "mobile" ? MOBILE_RE.test(target) : EMAIL_RE.test(target);

  async function send() {
    setSending(true);
    setError("");
    try {
      const { data } = await api.post("/api/auth/otp/send", { channel, target, purpose: "login" });
      setDevOtp(data?.dev_otp || "");
      setOtpLength(Number.isInteger(data?.otp_length) && data.otp_length >= 4 ? data.otp_length : 6);
      setDeliveredVia(data?.delivered_via || channel);
      setDeliveryTarget(data?.delivery_target || value);
      setCode("");
      setSent(true);
      restartCountdown();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  }

  function finishLogin(data) {
    if (data.access_token) setUserTokens(data.access_token, data.refresh_token);
    setUser(data.user, data.otp_verification_required);
    const otpRequired = data.otp_verification_required !== false;
    const signupDone = Boolean(data.user?.signup_complete && data.user?.email && data.user?.mobile_number);
    const needsOtpResume =
      !signupDone && otpRequired && (!data.user?.email_verified || !data.user?.mobile_verified);
    const needsMobileResume =
      !signupDone &&
      !otpRequired &&
      (!data.user?.email_verified || !data.user?.mobile_verified) &&
      !data.user?.mobile_number;
    if (needsOtpResume || needsMobileResume) {
      nav("/register", { replace: true });
    } else {
      nav(returnTo);
    }
  }

  async function verify(e) {
    e?.preventDefault();
    if (code.length < otpLength) {
      setError(`Enter all ${otpLength} digits to continue.`);
      return;
    }
    setVerifying(true);
    setError("");
    try {
      const payload = { channel, target, code };
      if (needsTotp) payload.totp_code = totp;
      const { data } = await api.post("/api/auth/otp/login", payload);
      finishLogin(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "totp_required") {
        setNeedsTotp(true);
      } else if (detail && typeof detail === "object" && detail.code === "kyc_incomplete") {
        setError(
          detail.message || "Please finish uploading your KYC documents to access your account.",
        );
      } else {
        const msg = getErrorMessage(err);
        setError(msg);
        if (!needsTotp || /otp/i.test(msg)) {
          setCode("");
          setOtpError((n) => n + 1);
        }
      }
    } finally {
      setVerifying(false);
    }
  }

  function changeTarget() {
    setSent(false);
    setNeedsTotp(false);
    setTotp("");
    setCode("");
    setDevOtp("");
    setDeliveredVia("");
    setDeliveryTarget("");
    setError("");
  }

  return (
    <AuthPageLayout layoutTestId="login-layout">
      <div
        className="glass-panel page-fade relative w-full overflow-hidden rounded-2xl"
        data-testid="login-card"
      >
        <div className="p-6">
          {new URLSearchParams(location.search).get("reason") === "idle" && (
            <p className="mb-4 rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs text-muted-foreground" data-testid="login-idle-notice">
              You were signed out after 10 minutes of inactivity (Safe session).
            </p>
          )}
          <div className="space-y-5">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Log in to PaisaCX</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                One-time code only. No passwords to remember or leak.
              </p>
            </div>

            {!sent ? (
              <form
                className="space-y-5"
                data-testid="login-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (valid && !sending) send();
                }}
              >
                <ChannelToggle
                  value={channel}
                  onChange={(c) => {
                    setChannel(c);
                    setValue("");
                    setError("");
                  }}
                />
                <div>
                  <label className={labelClass} htmlFor="login-id">
                    {channel === "mobile" ? "Mobile number" : "Email ID"}
                  </label>
                  <input
                    id="login-id"
                    className={fieldClass}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={channel === "mobile" ? "98765 43210" : "you@company.com"}
                    inputMode={channel === "mobile" ? "numeric" : "email"}
                    autoComplete={channel === "mobile" ? "tel-national" : "email"}
                    maxLength={channel === "mobile" ? 14 : 254}
                    data-testid="login-identifier"
                    autoFocus
                  />
                </div>
                <ErrorText msg={error} testId="login-error" />
                <button type="submit" className={ctaClass} disabled={!valid || sending} data-testid="login-send-otp">
                  {sending && <Loader2 size={16} className="animate-spin" />}
                  {sending ? "Sending code…" : "Send OTP"}
                </button>
              </form>
            ) : (
              <form className="space-y-4 page-fade" onSubmit={verify} data-testid="login-otp-form">
                <p className="text-sm text-muted-foreground">
                  {deliveredVia === "email" && channel === "mobile" ? (
                    <>
                      Mobile OTP is unavailable right now. We emailed a code to{" "}
                      <span className="text-foreground">{deliveryTarget || "your registered email"}</span>.{" "}
                      <strong className="text-foreground">Check your email</strong>
                      {!devOtp ? (
                        <>
                          {" "}
                          (and your <strong className="text-foreground">spam folder</strong>).
                        </>
                      ) : (
                        <>.</>
                      )}{" "}
                    </>
                  ) : (
                    <>
                      Code sent to <span className="text-foreground">{value}</span>.{" "}
                      {channel === "email" && !devOtp ? (
                        <>
                          Also check your <strong className="text-foreground">spam folder</strong>.{" "}
                        </>
                      ) : null}
                    </>
                  )}
                  <button type="button" className="text-primary hover:underline" onClick={changeTarget}>
                    Change
                  </button>
                </p>
                <DevOtpHint code={devOtp} />
                <OtpInput
                  key={otpError}
                  length={otpLength}
                  error={otpError > 0 && !!error}
                  testId="login-otp"
                  onChange={(c) => {
                    setCode(c);
                    setError("");
                  }}
                />

                {needsTotp && (
                  <div className="page-fade rounded-lg border border-border bg-surface-2 p-4">
                    <label htmlFor="login-totp" className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <KeyRound size={16} className="text-primary" />
                      Two-factor authentication
                    </label>
                    <input
                      id="login-totp"
                      inputMode="numeric"
                      maxLength={8}
                      value={totp}
                      onChange={(e) => setTotp(e.target.value.trim())}
                      data-testid="login-totp"
                      className={`${fieldClass} text-center font-mono tracking-[0.35em]`}
                      placeholder="000000"
                      autoFocus
                    />
                    <p className="mt-2 text-xs text-muted-foreground">
                      Enter the 6-digit code from your authenticator app or a backup code.{" "}
                      <Link to="/contact" className="text-primary hover:underline">
                        Lost 2FA access?
                      </Link>
                    </p>
                  </div>
                )}

                <ErrorText msg={error} testId="login-error" />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Code expires in 10 minutes.</span>
                  {left > 0 ? (
                    <span className="tabular-nums">Resend in 0:{String(left).padStart(2, "0")}</span>
                  ) : (
                    <button type="button" className="text-primary hover:underline" onClick={send} disabled={sending}>
                      Resend code
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  className={ctaClass}
                  disabled={verifying || code.length < otpLength || (needsTotp && totp.length < 6)}
                  data-testid="login-submit"
                >
                  {verifying && <Loader2 size={16} className="animate-spin" />}
                  {verifying ? "Verifying…" : "Verify & log in"}
                </button>
              </form>
            )}

            <p className="text-center text-sm text-muted-foreground">
              New here?{" "}
              <Link to="/register" className="text-primary hover:underline" data-testid="auth-to-register">
                Create an account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </AuthPageLayout>
  );
}
